import type {ApiFailure} from '@perfectpost/perfect-post-common';
import type {TFunction} from 'i18next';

/**
 * Le texte d'un refus de l'API (`ApiFailure`), dans la langue de l'ÉCRAN.
 *
 * Règle de priorité :
 *
 * 1. La traduction du code, `apiError.<code>`, interpolée avec les détails du refus
 *    (`page`, `owner`, `candidates`). C'est le cas normal.
 * 2. `failure.message`, le texte du serveur, seulement quand la clé manque (un code ajouté
 *    au catalogue avant sa traduction) — `defaultValue` de i18next, après le repli anglais.
 *
 * Exception : un refus fabriqué par le front (`apiFailureFrom`, `details.localMessage`)
 * porte un texte déjà écrit et traduit par l'écran, plus précis que la traduction du code :
 * il s'affiche tel quel.
 *
 * Pourquoi la traduction du front passe AVANT le texte du serveur, y compris pour les refus
 * de page que le serveur sait écrire en français, anglais et espagnol : le serveur écrit
 * dans la langue du PROFIL (`userSession.locale`), qui n'est pas forcément celle de
 * l'interface, et en anglais pour tout le reste du catalogue (« The free plan is limited to
 * 1 scheduled post per week » montré à un utilisateur français). Ce qui rend ses messages de
 * page plus précis — le nom de la page, celui du porteur, les pages candidates — lui arrive
 * aussi dans `details` : la traduction du front les reprend, elle est donc aussi précise ET
 * toujours dans la bonne langue.
 */
export function apiFailureMessage(failure: ApiFailure, t: TFunction): string {
  const {page, owner, candidates, localMessage} = failure.details;
  if (localMessage === true) {
    return failure.message;
  }
  const text = t(`apiError.${failure.code}`, {
    page: namedValue(page) ?? t('apiFailure.page'),
    owner: namedValue(owner) ?? t('apiFailure.owner'),
    candidates: Array.isArray(candidates)
      ? candidates
          .map(namedValue)
          .filter((name): name is string => name !== undefined)
          .join(', ')
      : '',
    defaultValue: failure.message,
  });
  if (text === failure.message) {
    return text;
  }
  // Le porteur ouvre certaines phrases : « le porteur de la page… » prend sa majuscule.
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Le `name` d'un détail `{id, name?}` (page, porteur, candidate), quand il est connu. */
function namedValue(detail: unknown): string | undefined {
  if (typeof detail !== 'object' || detail === null) {
    return undefined;
  }
  const {name} = detail as {name?: unknown};
  return typeof name === 'string' && name.trim() !== '' ? name : undefined;
}
